import React from "react";
import { Store, Building2, Compass, Users, Award, Phone, Mail } from "lucide-react";
import { Link } from "react-router-dom";

export default function About() {
  return (
    <div className="bg-white" data-testid="about-page">
      {/* HERO */}
      <section className="container-zelo py-16 md:py-24">
        <div className="max-w-3xl">
          <div className="label-tiny text-brand">About Zelo</div>
          <h1 className="font-heading text-4xl md:text-6xl font-medium tracking-tight mt-3 text-balance">
            Two niches. <span className="text-brand">One specialist house.</span>
          </h1>
          <p className="mt-6 text-lg text-neutral-500 leading-relaxed">
            Zelo is a focused real-estate practice operating at the two narrowest, hardest-to-execute corners of Indian commercial real estate — <span className="font-medium text-neutral-800">brand-fit retail leasing</span> and <span className="font-medium text-neutral-800">pre-leased investment-grade assets</span>. We don't do everything. We do these two things deeply.
          </p>
        </div>
      </section>

      {/* VISION */}
      <section className="bg-neutral-50/60 py-16 md:py-24">
        <div className="container-zelo grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <div className="w-12 h-12 rounded-xl bg-brand text-white flex items-center justify-center mb-5"><Compass className="w-5 h-5" /></div>
            <div className="label-tiny text-brand">Our Vision</div>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-2">Make commercial real estate <em className="not-italic text-brand">predictable</em> in India.</h2>
          </div>
          <div className="md:col-span-8 space-y-5 text-neutral-600 leading-relaxed text-lg" data-testid="about-vision">
            <p>
              India's brand expansion teams and serious capital allocators have lived with the same friction for two decades — opaque inventory, broker chains four deep, possession dates that slip a quarter, and lease docs that need a second legal eye to trust.
            </p>
            <p>
              Zelo exists to remove that friction. We build the tooling, curate the inventory, and underwrite the documentation so a national retail rollout, or a ₹5–50 Cr pre-leased buy, can move at the speed of a mutual-fund decision — not a six-month treasure hunt.
            </p>
          </div>
        </div>
      </section>

      {/* TWO EXPERTISES */}
      <section className="container-zelo py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="label-tiny text-brand">What We Do</div>
          <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3">Two specialist desks. <span className="text-brand">No overlap.</span></h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="about-expertise-grid">
          {/* Retail Leasing */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-8 hover:-translate-y-1 transition-all duration-300" data-testid="about-retail-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand flex items-center justify-center"><Store className="w-5 h-5" /></div>
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full bg-brand text-white">Primary</span>
            </div>
            <h3 className="font-heading text-2xl font-medium mt-5">Retail Leasing</h3>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              We curate brand-fit retail real estate — high-street frontages, premium mall spaces, and emerging catchments — and pair them with the brand expansion teams that need them.
            </p>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              Every space carries verified carpet area, frontage, height, possession calendar and asking rent before it goes live. No broker telephone, no possession surprises, no wasted site visits.
            </p>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              <span className="font-medium text-neutral-900">1,070+ curated spaces</span> across 15+ states and 25+ cities, refreshed weekly by our acquisition desk.
            </p>
            <Link to="/retail-leasing" className="inline-flex items-center gap-2 mt-6 text-brand font-semibold text-sm hover:gap-3 transition-all" data-testid="about-retail-cta">Explore Retail Leasing →</Link>
          </div>

          {/* Pre-Leased */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-8 hover:-translate-y-1 transition-all duration-300" data-testid="about-preleased-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800">Beta</span>
            </div>
            <h3 className="font-heading text-2xl font-medium mt-5">Pre-Leased Investments</h3>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              We curate commercial properties already rented to credible brand tenants — generating verified, signed, bank-eligible rental income from Day 1.
            </p>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              Every asset on our desk goes through legal, technical and lease-strength due diligence before it reaches our investor list. We surface only what we'd own ourselves.
            </p>
            <p className="mt-3 text-neutral-600 leading-relaxed">
              Built for <span className="font-medium text-neutral-900">HNIs, NRIs and family offices</span> chasing 8–9% rent yields with LRD-eligible documentation and zero vacancy risk.
            </p>
            <Link to="/pre-leased" className="inline-flex items-center gap-2 mt-6 text-brand font-semibold text-sm hover:gap-3 transition-all" data-testid="about-preleased-cta">Explore Pre-Leased →</Link>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-neutral-50/60 py-16 md:py-24">
        <div className="container-zelo">
          <div className="max-w-2xl mb-12">
            <div className="label-tiny text-brand">The Team</div>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight mt-3">Small house. <span className="text-brand">Senior bench.</span></h2>
            <p className="mt-4 text-lg text-neutral-500 leading-relaxed">
              Zelo is built by a tight team of retail-leasing veterans, investment underwriters and product engineers — every transaction touches a senior partner, not a junior associate.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="about-team-grid">
            <div className="rounded-2xl bg-white border border-neutral-200 p-7" data-testid="about-team-aman">
              <div className="w-14 h-14 rounded-full bg-brand text-white font-heading text-xl font-medium flex items-center justify-center">AN</div>
              <div className="font-heading text-xl font-medium mt-5">Aman Nainani</div>
              <div className="text-sm text-brand font-semibold mt-1">Chief Sales Officer</div>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                Leads brand & investor sales across Zelo's retail and pre-leased desks. A decade in Indian commercial real estate spanning brand leasing, pre-leased investments and developer advisory.
              </p>
              <div className="flex flex-col gap-1.5 mt-4 text-sm">
                <a href="tel:+918758115155" className="inline-flex items-center gap-2 text-neutral-600 hover:text-brand"><Phone className="w-3.5 h-3.5" /> +91 87581 15155</a>
                <a href="mailto:aman@zelolease.com" className="inline-flex items-center gap-2 text-neutral-600 hover:text-brand"><Mail className="w-3.5 h-3.5" /> aman@zelolease.com</a>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-neutral-200 p-7" data-testid="about-team-kunal">
              <div className="w-14 h-14 rounded-full bg-brand text-white font-heading text-xl font-medium flex items-center justify-center">KM</div>
              <div className="font-heading text-xl font-medium mt-5">Kunal Mulchandani</div>
              <div className="text-sm text-brand font-semibold mt-1">Chief Acquisition Officer</div>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                Heads inventory acquisition — hand-picks every brand-fit retail frontage and pre-leased asset that lands on the Zelo desk. Personally underwrites tenant credit and lease strength.
              </p>
              <div className="flex flex-col gap-1.5 mt-4 text-sm">
                <a href="tel:+91958624363" className="inline-flex items-center gap-2 text-neutral-600 hover:text-brand"><Phone className="w-3.5 h-3.5" /> +91 9586 24363</a>
                <a href="mailto:km@zelolease.com" className="inline-flex items-center gap-2 text-neutral-600 hover:text-brand"><Mail className="w-3.5 h-3.5" /> km@zelolease.com</a>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-neutral-200 p-7" data-testid="about-team-karan">
              <div className="w-14 h-14 rounded-full bg-brand text-white font-heading text-xl font-medium flex items-center justify-center">KN</div>
              <div className="font-heading text-xl font-medium mt-5">Karan Nainani</div>
              <div className="text-sm text-brand font-semibold mt-1">Chief Operations Officer</div>
              <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
                Runs everything between handshake and possession — legal, technical, lease execution, and client onboarding. Makes sure transactions close on the timeline they were promised.
              </p>
              <div className="flex flex-col gap-1.5 mt-4 text-sm">
                <a href="tel:+917069707520" className="inline-flex items-center gap-2 text-neutral-600 hover:text-brand"><Phone className="w-3.5 h-3.5" /> +91 70697 07520</a>
                <a href="mailto:karan@zelolease.com" className="inline-flex items-center gap-2 text-neutral-600 hover:text-brand"><Mail className="w-3.5 h-3.5" /> karan@zelolease.com</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RECOGNITION + CTA */}
      <section className="container-zelo py-16 md:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-50 text-brand text-xs font-bold tracking-[0.2em] uppercase">
            <Award className="w-3.5 h-3.5" /> Recognition
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight text-balance mt-5">
            Recognised by <span className="text-brand">IIM-A Director, Mr. Praful Dholakia.</span>
          </h2>
          <p className="mt-5 text-neutral-500 text-lg leading-relaxed">
            We're rebuilding how India transacts in commercial real estate — making it as predictable as a corporate lease and as transparent as a public mutual fund.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/retail-leasing" className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-7 py-3.5 text-sm font-semibold hover:bg-brand-600 transition shadow-brand active:scale-95" data-testid="about-cta-retail">
              <Store className="w-4 h-4" /> Explore Retail Leasing
            </Link>
            <Link to="/pre-leased" className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-7 py-3.5 text-sm font-semibold hover:border-brand hover:text-brand transition" data-testid="about-cta-preleased">
              <Building2 className="w-4 h-4" /> Explore Pre-Leased
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
